// Executive Live mock data (approved HTML:
// 12-Executive-Live-Consistency-Review-v4.html). Sample company
// scorecard for the executive rail. All numbers illustrative; the real
// snapshot comes from services/manager-operations.

export interface Scorecard {
  key: string;
  label: string;
  value: string;
  delta: string;
  caption: string;
  target?: string;
}

export const PERIOD_NOTE = 'Oct 1–5, 2026 vs Sep 1–5, 2026';
export const SNAPSHOT_AT = 'As of Oct 5 · 2:30 PM CT';

export const SCORECARDS: Scorecard[] = [
  {
    key: 'net-sales',
    label: 'COMPANY NET SALES',
    value: '$458,240',
    delta: '↑ +7.2%',
    caption: 'All company channels · CRM sales shown separately',
    target: 'Sample target $476,000',
  },
  {
    key: 'margin',
    label: 'GROSS MARGIN',
    value: '64.2%',
    delta: '↑ +1.1 pts',
    caption: 'Illustrative finance metric',
    target: 'Sample target 63%',
  },
  {
    key: 'sub-revenue',
    label: 'SUBSCRIPTION REVENUE',
    value: '$219,450',
    delta: '↑ +9.4%',
    caption: 'Collected subscription revenue · sample',
    target: 'Sample target $210,000',
  },
  {
    key: 'sub-growth',
    label: 'NET SUBSCRIBER GROWTH',
    value: '+185',
    delta: '↑ +18.6%',
    caption: 'Unique customers · separate from contracts',
  },
  {
    key: 'contact-rate',
    label: 'CONTACT RATE',
    value: '21.7%',
    delta: '↑ +2.8 pts',
    caption: 'Reached ÷ attempted customers · sample',
  },
  {
    key: 'close-rate',
    label: 'CLOSE RATE',
    value: '18.4%',
    delta: '↑ +1.9 pts',
    caption: 'Credited orders ÷ reached customers · sample',
  },
];

export interface TrendMonth {
  label: string;
  value: string;
  height: number;
  current?: boolean;
}

export const TREND_MONTHS: TrendMonth[] = [
  { label: 'Apr', value: '$2.24M', height: 97 },
  { label: 'May', value: '$2.31M', height: 100 },
  { label: 'Jun', value: '$2.46M', height: 107 },
  { label: 'Jul', value: '$2.58M', height: 112 },
  { label: 'Aug', value: '$2.72M', height: 118 },
  { label: 'Sep', value: '$2.79M', height: 121, current: true },
];

export const MTD_RESULT = {
  value: '$458,240',
  caption: '96.3% of the sample $476,000 target.',
  lines: [
    { label: 'Gross margin', value: '64.2%' },
    { label: 'Gross profit · sample', value: '$294,190' },
    { label: 'Prior comparable sales', value: '$427,463' },
    { label: 'Sales change', value: '+7.2%' },
  ],
};

export const SUBSCRIPTION_TILES = [
  { label: 'ACTIVE SUBSCRIBERS', value: '14,820', caption: 'Unique customers · period end' },
  { label: 'ACTIVE SUBSCRIPTIONS', value: '18,460', caption: 'Contracts · period end' },
  { label: 'SUBSCRIPTION REVENUE', value: '$219,450', caption: 'Collected in selected period' },
  { label: 'RENEWAL SUCCESS', value: '96.8%', caption: '1,258 / 1,300 due renewals' },
];

export const SUBSCRIPTION_ROWS = [
  { label: 'Newly active subscribers', value: '228' },
  { label: 'Fully cancelled subscribers', value: '43' },
  { label: 'Net subscriber growth', value: '+185' },
  { label: 'Subscriber retention', value: '99.7%' },
  { label: 'Paused contracts', value: '318' },
  { label: 'Payment recovery', value: '87.6%' },
  { label: 'Contracts / subscriber', value: '1.25' },
];

export const EFFECTIVENESS_ROWS = [
  { team: 'Inside Sales', contact: '22.4%', close: '19.1%', rpc: '$54', sales: '$68,320' },
  { team: 'Vet Program', contact: '18.9%', close: '16.8%', rpc: '$59', sales: '$25,550' },
  { team: 'Retention', contact: '24.1%', close: '17.4%', rpc: '$47', sales: '$11,340' },
  { team: 'CRM total', contact: '21.7%', close: '18.4%', rpc: '$54', sales: '$105,210' },
];

export type HealthTone = 'good' | 'warn' | 'default';

export interface HealthStat {
  label: string;
  value: string;
  caption: string;
  tone: HealthTone;
}

export const CUSTOMER_HEALTH: HealthStat[] = [
  { label: 'NEW CUSTOMERS', value: '1,002', caption: '↑ +11.2%', tone: 'good' },
  { label: 'REPEAT CUSTOMER RATE', value: '41.3%', caption: '↑ +2.1 pts', tone: 'default' },
  { label: 'CONFIRMED RETENTIONS', value: '78', caption: 'Customer-level outcomes', tone: 'good' },
  { label: 'REVENUE RECOVERED', value: '$28,600', caption: 'Payment + retention recovery', tone: 'default' },
  { label: '90-DAY CUSTOMER VALUE', value: '$612', caption: 'Sample cohort value', tone: 'default' },
  { label: 'AT-RISK SUBSCRIBERS', value: '326', caption: 'Snapshot at period end', tone: 'warn' },
];

export const SERVICE_HEALTH: HealthStat[] = [
  { label: 'FIRST RESPONSE', value: '2m 42s', caption: 'Median · eligible inbound', tone: 'good' },
  { label: 'SLA ADHERENCE', value: '96.4%', caption: 'Within sample channel targets', tone: 'good' },
  { label: 'HIGH-PRIORITY OPEN', value: '17', caption: 'Snapshot at period end', tone: 'warn' },
  { label: 'AUTO-ROUTED TICKETS', value: '298', caption: 'Selected period', tone: 'default' },
  { label: 'CONFIRMED RESOLUTION', value: '93.1%', caption: 'Resolved customer cases', tone: 'default' },
  { label: 'ESCALATIONS OVERDUE', value: '2', caption: 'Snapshot at period end', tone: 'warn' },
];

export interface AttentionItem {
  idx: number;
  severity: 'red' | 'amber' | 'none';
  title: string;
  body: string;
}

export const ATTENTION_ITEMS: AttentionItem[] = [
  {
    idx: 0,
    severity: 'red',
    title: '2 service escalations overdue',
    body: 'Both have assigned owners. Oldest is 37 minutes past the sample target.',
  },
  {
    idx: 1,
    severity: 'none',
    title: 'Subscription snapshot needs refresh',
    body: 'Totals show a sample snapshot that is 14 minutes stale.',
  },
  {
    idx: 2,
    severity: 'none',
    title: 'Cancellation rate +0.6 pts',
    body: 'Above the comparable prior period. Review the customer reasons.',
  },
];

export const INSIGHTS_ROWS = [
  { label: 'Net sales', value: '+7.2%', fresh: true },
  { label: 'Subscription revenue', value: '+9.4%', fresh: true },
  { label: 'Net subscribers', value: '+18.6%', fresh: true },
  { label: 'Contact rate', value: '+2.8 pts', fresh: true },
  { label: 'Close rate', value: '+1.9 pts', fresh: true },
  { label: 'High-priority tickets', value: '17 open', fresh: false },
];

export const SOURCE_CONFIDENCE = [
  { label: 'Company sales', value: '4 min ago', fresh: true, stale: false },
  { label: 'Finance margin', value: '18 min ago', fresh: true, stale: false },
  { label: 'Subscriptions', value: '14 min stale', fresh: false, stale: true },
  { label: 'Sales activity', value: '2 min ago', fresh: true, stale: false },
  { label: 'Customer Service', value: '1 min ago', fresh: true, stale: false },
];

export const PROGRAM_PULSE = [
  { label: 'Colic Coverage enrollments', value: '31' },
  { label: 'Under-dosed upgrades', value: '54' },
  { label: 'Reactivations', value: '67' },
  { label: 'New vet affiliates', value: '4' },
];
